import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { FormDialog } from "@/components/common/FormDialog";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { Avatar } from "@/components/common/Avatar";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SearchInput } from "@/components/common/SearchInput";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState, useMemo } from "react";
import { BarChart3, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export function Dashboards() {
  const qc = useQueryClient();
  const { data: dashboards, isLoading } = useQuery({ queryKey: ["dashboards"], queryFn: () => get("/reporting/dashboards") });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const umap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const create = useMutation({ mutationFn: (b) => post("/reporting/dashboards", b), onSuccess: () => { qc.invalidateQueries({ queryKey: ["dashboards"] }); toast.success("Dashboard created"); } });

  return (
    <div>
      <PageHeader title="Dashboards" subtitle="Custom analytics dashboards.">
        <FormDialog title="New Dashboard" testid="new-dashboard-modal"
          trigger={<Button data-testid="new-dashboard"><Plus className="mr-1.5 h-4 w-4" />New Dashboard</Button>}
          fields={[
            { name: "name", label: "Dashboard Name", required: true, full: true },
            { name: "visibility", label: "Visibility", type: "select", options: ["Private", "Department", "Company-wide"] },
          ]}
          onSubmit={(v) => create.mutateAsync({ ...v, owner: "u-avery" })} />
      </PageHeader>
      {isLoading ? <Skeleton className="h-64" /> : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {dashboards?.map((d) => (
            <div key={d.id} data-testid={`dashboard-${d.id}`} className="rounded-xl border border-border bg-card p-4 hover:border-primary/40 hover:shadow-sm">
              <div className="flex h-24 items-center justify-center rounded-lg bg-muted"><BarChart3 className="h-8 w-8 text-muted-foreground" /></div>
              <h3 className="mt-3 font-display text-sm font-semibold">{d.name}</h3>
              <div className="mt-2 flex items-center justify-between">
                <Avatar user={umap[d.owner]} size="xs" />
                <StatusBadge family="slate" label={d.visibility} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function StandardReports() {
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");
  const { data: reports, isLoading } = useQuery({ queryKey: ["std-reports"], queryFn: () => get("/reporting/standard") });
  const modules = useMemo(() => [...new Set((reports || []).map((r) => r.module).filter(Boolean))], [reports]);
  const filtered = useMemo(() => {
    return (reports || []).filter((r) => {
      if (search && !r.name?.toLowerCase().includes(search.toLowerCase())) return false;
      if (moduleFilter !== "all" && r.module !== moduleFilter) return false;
      return true;
    });
  }, [reports, search, moduleFilter]);

  return (
    <div>
      <PageHeader title="Standard Reports" subtitle="Pre-built reports across modules." />
      {reports?.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <SearchInput testid="report-search" placeholder="Search report name" value={search} onChange={setSearch} />
          <Select value={moduleFilter} onValueChange={setModuleFilter}>
            <SelectTrigger className="w-40" data-testid="report-module-filter"><SelectValue placeholder="All modules" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All modules</SelectItem>
              {modules.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}
      {isLoading ? <Skeleton className="h-64" /> : !reports?.length ? (
        <EmptyState icon={BarChart3} title="No reports yet" />
      ) : !filtered.length ? (
        <EmptyState icon={BarChart3} title="No reports match" description="Adjust your search or filter." />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-semibold">Report</th><th className="px-4 py-3 font-semibold">Module</th>
              <th className="px-4 py-3 font-semibold">Last Run</th><th className="px-4 py-3 text-right font-semibold">Export</th></tr></thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`report-${r.id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                  <td className="px-4 py-3 font-medium">{r.name}</td>
                  <td className="px-4 py-3"><StatusBadge family="blue" label={r.module} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{new Date(r.last_run).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-right"><Button size="sm" variant="ghost">Export</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
